import React, { useMemo } from 'react';
import { Files, Pratica } from "../types";
import {
    Car, Clock, Euro, TrendingUp, User, MapPin,
    Briefcase, ShieldAlert, Target, Scale,
    ShieldCheck, ExternalLink, Copy, FileText
} from "lucide-react";
import { mandatiList } from "../utils/mandatiData";
import { formatRole } from "../utils/utils";

const fmtEur = (n: number) => `€ ${(Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export interface TabRiepilogoProps {
    pratica: Pratica;
    teamStats: any[];
    fileHistory?: Files[];
}

export const TabRiepilogo: React.FC<TabRiepilogoProps> = ({ pratica, teamStats }) => {

    // Dati JSON generici del mandato
    const datiAggiuntivi = useMemo(() => {
        const p = pratica as any;
        if (p.datiAggiuntiviJson && typeof p.datiAggiuntiviJson === 'string') {
            try { return JSON.parse(p.datiAggiuntiviJson); } catch(e) { return {}; }
        }
        return p.datiAggiuntiviJson || {};
    }, [pratica]);

    // Soggetti sempre come array, anche per i mandati vecchi a soggetto singolo
    const soggetti = useMemo(() => {
        if (datiAggiuntivi.soggetti && Array.isArray(datiAggiuntivi.soggetti) && datiAggiuntivi.soggetti.length > 0) {
            return datiAggiuntivi.soggetti;
        }

        // Fallback per vecchi mandati
        if (datiAggiuntivi.soggetto_nome || datiAggiuntivi.soggetto_denominazione) {
            return [{
                tipo: datiAggiuntivi.soggetto_tipo || 'Privato',
                nome: datiAggiuntivi.soggetto_nome || datiAggiuntivi.soggetto_denominazione,
                nato_a: datiAggiuntivi.soggetto_nato_a,
                data_nascita: datiAggiuntivi.soggetto_data_nascita,
                codice_fiscale: datiAggiuntivi.soggetto_codice_fiscale,
                piva: datiAggiuntivi.soggetto_piva,
                sede_via: datiAggiuntivi.soggetto_sede_via,
                sede_citta: datiAggiuntivi.soggetto_sede_citta,
                sede_cap: datiAggiuntivi.soggetto_sede_cap,
                sede_prov: datiAggiuntivi.soggetto_sede_prov,
                indirizzo: datiAggiuntivi.soggetto_indirizzo,
                citta: datiAggiuntivi.soggetto_citta,
                cap: datiAggiuntivi.soggetto_cap,
                prov: datiAggiuntivi.soggetto_prov,
                domicilio_altrove: datiAggiuntivi.soggetto_domicilio_altrove,
                auto: datiAggiuntivi.soggetto_auto || datiAggiuntivi.soggetto_mezzi,
                auto_conoscenti: datiAggiuntivi.soggetto_auto_conoscenti,
                moto: datiAggiuntivi.soggetto_moto,
                fisico: datiAggiuntivi.soggetto_fisico,
                lavoro: datiAggiuntivi.soggetto_lavoro,
                telefono: datiAggiuntivi.soggetto_telefono,
                luoghi_frequentati: datiAggiuntivi.soggetto_luoghi_frequentati,
                hobby: datiAggiuntivi.soggetto_hobby,
                abitudini: datiAggiuntivi.soggetto_abitudini,
                // Alert del formato vecchio
                pericoloso: datiAggiuntivi.soggetto_pericoloso,
                armi: datiAggiuntivi.soggetto_armi,
                attento: datiAggiuntivi.soggetto_attento,
                gia_pedinato: datiAggiuntivi.soggetto_gia_pedinato,
                lavoro_particolare: datiAggiuntivi.soggetto_lavoro_particolare,
            }];
        }
        return [];
    }, [datiAggiuntivi]);

    // Titolo mandato
    const titoloMandato = useMemo(() => {
        if (datiAggiuntivi.interesse_diritto_tutelato) return datiAggiuntivi.interesse_diritto_tutelato;
        const mandato = mandatiList.find((m: any) => m.code === datiAggiuntivi.codice_mandato);
        return mandato ? mandato.title : datiAggiuntivi.codice_mandato;
    }, [datiAggiuntivi.codice_mandato, datiAggiuntivi.interesse_diritto_tutelato]);

    const getSoggettoAlerts = (s: any): string[] => {
        const list: string[] = [];
        if (s.pericoloso) list.push('PERICOLOSO');
        if (s.armi)       list.push('POSSIBILE ARMATO');
        if (s.attento)    list.push('MOLTO ATTENTO');
        if (s.gia_pedinato) list.push('GIÀ OSSERVATO/PEDINATO');
        if (s.lavoro_particolare) list.push('ATTIVITÀ LAVORATIVA PARTICOLARE');
        return list;
    };

    return (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 animate-in fade-in duration-300 pb-10">

            {/* Colonna sinistra: dati pratica e soggetti */}
            <div className="xl:col-span-2 space-y-6">


                {/* Oggetto e scopo dell'indagine */}
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center gap-3">
                        <div className="p-2 bg-slate-200 text-slate-700 rounded-xl"><Scale size={20}/></div>
                        <div>
                            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-0.5">Scopo dell'Indagine</h3>
                            <div className="text-lg font-black text-slate-900 leading-tight uppercase">
                                {titoloMandato ? `MANDATO: ${titoloMandato}` : 'Dettagli Incarico'}
                            </div>
                        </div>
                    </div>
                    <div className="p-5 space-y-4">
                        {datiAggiuntivi.oggetto_mandato_text && (
                            <div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Diritto da Tutelare / Oggetto</span>
                                <div className="text-sm font-medium text-slate-800 bg-slate-50 p-4 rounded-xl border border-slate-100 whitespace-pre-wrap leading-relaxed">
                                    {datiAggiuntivi.oggetto_mandato_text}
                                </div>
                            </div>
                        )}
                        {pratica.note && (
                            <div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Note e Direttive Interne (Ufficio)</span>
                                <div className="text-sm font-medium text-blue-900 bg-blue-50 p-4 rounded-xl border border-blue-100 whitespace-pre-wrap leading-relaxed italic">
                                    {pratica.note}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Soggetti investigati */}
                {soggetti.map((soggetto: any, idx: number) => {
                    const isAzienda = soggetto.tipo === 'Azienda';
                    const via = isAzienda ? soggetto.sede_via : soggetto.indirizzo;
                    const citta = isAzienda ? soggetto.sede_citta : soggetto.citta;
                    const cap = isAzienda ? soggetto.sede_cap : soggetto.cap;
                    const prov = isAzienda ? soggetto.sede_prov : soggetto.prov;
                    const soggettoAlerts = getSoggettoAlerts(soggetto);
                    const hasAlerts = soggettoAlerts.length > 0;

                    return (
                        <div key={idx} className={`bg-white rounded-2xl overflow-hidden shadow-sm border ${hasAlerts ? 'border-rose-300' : 'border-slate-200'}`}>
                            <div className={`border-b px-5 py-4 flex items-center gap-3 ${hasAlerts ? 'bg-rose-50 border-rose-200' : 'bg-orange-50 border-orange-100'}`}>
                                <div className={`p-2 rounded-xl ${hasAlerts ? 'bg-rose-100 text-rose-600' : 'bg-orange-100 text-orange-600'}`}>
                                    {hasAlerts ? <ShieldAlert size={20} className="animate-pulse" /> : <Target size={20} />}
                                </div>
                                <div className="flex-1">
                                    <h3 className={`text-[10px] font-bold uppercase tracking-widest mb-0.5 ${hasAlerts ? 'text-rose-600' : 'text-orange-600'}`}>
                                        Soggetto Investigato {soggetti.length > 1 ? ` N. ${idx + 1}` : ''}
                                    </h3>
                                    <div className="text-xl font-black text-slate-900 leading-tight uppercase">
                                        {soggetto?.nome || 'N/D'}
                                        {isAzienda && <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-2 py-1 rounded uppercase tracking-wider font-bold">Azienda</span>}
                                    </div>
                                    {hasAlerts && (
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                            {soggettoAlerts.map((a, i) => (
                                                <span key={i} className="bg-rose-600 text-white text-[9px] font-black px-2.5 py-1 rounded uppercase tracking-wider">
                                                    {a}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="divide-y divide-slate-100">
                                {/* Dati Base */}
                                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50/50">
                                    {!isAzienda && soggetto?.nato_a && (
                                        <div>
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Nato/a a</span>
                                            <span className="text-sm font-semibold text-slate-800">{soggetto.nato_a} {soggetto.data_nascita ? `il ${new Date(soggetto.data_nascita).toLocaleDateString()}` : ''}</span>
                                        </div>
                                    )}
                                    {(soggetto?.codice_fiscale || soggetto?.piva) && (
                                        <div>
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">{isAzienda ? 'P.IVA / CF' : 'Codice Fiscale'}</span>
                                            <span className="text-sm font-semibold text-slate-800 uppercase">{soggetto.piva || soggetto.codice_fiscale}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Indirizzi */}
                                <div className="p-5 flex gap-4 items-start hover:bg-slate-50 transition-colors">
                                    <div className="mt-0.5 text-blue-500 bg-blue-50 p-2.5 rounded-xl"><MapPin size={18}/></div>
                                    <div className="flex-1 space-y-3">
                                        <div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{isAzienda ? 'Via Sede Legale' : 'Indirizzo Principale'}</div>
                                            <div className="text-sm font-semibold text-slate-800">
                                                {via || 'Indirizzo non specificato'}
                                                {citta && ` - ${cap || ''} ${citta} ${prov ? `(${prov})` : ''}`}
                                            </div>
                                        </div>
                                        {soggetto?.domicilio_altrove && (
                                            <div>
                                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Domicilio Altrove / Altre Pertinenze</div>
                                                <div className="text-sm text-slate-800 font-medium whitespace-pre-wrap bg-slate-100 p-2 rounded-lg">{soggetto.domicilio_altrove}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Profilo Operativo */}
                                <div className="p-5 flex gap-4 items-start hover:bg-slate-50 transition-colors">
                                    <div className="mt-0.5 text-indigo-500 bg-indigo-50 p-2.5 rounded-xl"><Briefcase size={18}/></div>
                                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {/* Veicoli divisi come nel mandato (propri, di famigliari, moto);
                                            il blocco compare se almeno uno è valorizzato. */}
                                        {(soggetto?.auto || soggetto?.auto_conoscenti || soggetto?.moto) && (
                                            <div className="sm:col-span-2 space-y-2">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Veicoli Segnalati</span>
                                                {soggetto?.auto && (
                                                    <div>
                                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Di proprietà</span>
                                                        <span className="text-sm font-semibold text-indigo-700 uppercase bg-indigo-50 px-3 py-2 rounded-lg block whitespace-pre-wrap">{soggetto.auto}</span>
                                                    </div>
                                                )}
                                                {soggetto?.auto_conoscenti && (
                                                    <div>
                                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Di famigliari</span>
                                                        <span className="text-sm font-medium text-slate-800 uppercase bg-slate-100 px-3 py-2 rounded-lg block whitespace-pre-wrap">{soggetto.auto_conoscenti}</span>
                                                    </div>
                                                )}
                                                {soggetto?.moto && (
                                                    <div>
                                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Motocicli</span>
                                                        <span className="text-sm font-medium text-slate-800 uppercase bg-slate-100 px-3 py-2 rounded-lg block whitespace-pre-wrap">{soggetto.moto}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {soggetto?.fisico && (
                                            <div className="sm:col-span-2">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Caratteristiche Fisiche</span>
                                                <span className="text-sm font-medium text-slate-800 block whitespace-pre-wrap">{soggetto.fisico}</span>
                                            </div>
                                        )}
                                        {soggetto?.lavoro && <div><span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Attività Lavorativa</span><span className="text-sm font-medium text-slate-800">{soggetto.lavoro}</span></div>}
                                        {soggetto?.telefono && <div><span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Recapiti Telefonici</span><span className="text-sm font-medium text-slate-800">{soggetto.telefono}</span></div>}
                                        {soggetto?.luoghi_frequentati && (
                                            <div className="sm:col-span-2 mt-2">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Luoghi Frequentati</span>
                                                <span className="text-sm font-medium text-slate-700 italic block bg-slate-100 p-2 rounded">"{soggetto.luoghi_frequentati}"</span>
                                            </div>
                                        )}
                                        {soggetto?.hobby && (
                                            <div className="sm:col-span-2">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Hobby e Sport</span>
                                                <span className="text-sm font-medium text-slate-700 block bg-slate-100 p-2 rounded whitespace-pre-wrap">{soggetto.hobby}</span>
                                            </div>
                                        )}
                                        {soggetto?.abitudini && (
                                            <div className="sm:col-span-2">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Abitudini / Orari Tipici</span>
                                                <span className="text-sm font-medium text-slate-700 block bg-slate-100 p-2 rounded whitespace-pre-wrap">{soggetto.abitudini}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>


            {/* Colonna destra: team e statistiche */}
            <div className="space-y-6">
                {/* Verifica firma digitale: solo se OTP Service ha restituito il checksum.
                    Il link porta alla pagina pubblica di verifica e download del PDF firmato. */}
                {(pratica.otpFinalChecksumMandato || pratica.otpFinalChecksumSoggetto) && (
                    <BloccoVerificaFirma
                        checksumMandato={pratica.otpFinalChecksumMandato}
                        checksumSoggetto={pratica.otpFinalChecksumSoggetto}
                    />
                )}

                {/* Team e costi */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden sticky top-[90px]">
                    <div className="bg-slate-50 border-b border-slate-200 px-5 py-4">
                        <h4 className="font-bold text-slate-800 uppercase tracking-wider text-xs flex items-center gap-2">
                            <User size={16} className="text-blue-500"/> Costo Team Operativo
                        </h4>
                    </div>

                    <div className="p-5">
                        {teamStats.length > 0 ? (
                            <div className="space-y-5">
                                {teamStats.map((stat, idx) => (
                                    <div key={idx} className="pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center text-blue-700 font-black text-xs shadow-sm border border-blue-200">
                                                {stat.user.name.charAt(0)}{stat.user.surname.charAt(0)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-bold text-slate-900 truncate">{stat.user.name} {stat.user.surname}</div>
                                                <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{formatRole(stat.user.role)}</div>
                                            </div>
                                            <div className="text-[9px] font-black bg-blue-50 text-blue-600 px-2 py-1 rounded border border-blue-100 whitespace-nowrap">
                                                {stat.incarichiCount} inc.
                                            </div>
                                        </div>

                                        <div className="bg-slate-50 rounded-xl border border-slate-100 divide-y divide-slate-100 text-[10px]">
                                            <div className="flex justify-between items-center px-3 py-1.5">
                                                <span className="flex items-center gap-1 text-slate-500 font-bold uppercase"><Clock size={10}/> Ore ({stat.displayTime.h}h {stat.displayTime.m}m × {fmtEur(stat.costoOrario)}/h)</span>
                                                <span className="font-black text-slate-700">{fmtEur(stat.totalOre * (stat.costoOrario||0))}</span>
                                            </div>
                                            {stat.totalKm > 0 && (
                                                <div className="flex justify-between items-center px-3 py-1.5">
                                                    <span className="flex items-center gap-1 text-slate-500 font-bold uppercase"><Car size={10}/> Km ({stat.totalKm} km × {fmtEur(stat.rimborsoKm)}/km)</span>
                                                    <span className="font-black text-slate-700">{fmtEur(stat.totalKm * (stat.rimborsoKm||0))}</span>
                                                </div>
                                            )}
                                            {stat.totalSpese > 0 && (
                                                <div className="flex justify-between items-center px-3 py-1.5">
                                                    <span className="flex items-center gap-1 text-slate-500 font-bold uppercase"><Euro size={10}/> Spese rimborsate</span>
                                                    <span className="font-black text-slate-700">{fmtEur(stat.totalSpese)}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between items-center px-3 py-2 bg-emerald-50 rounded-b-xl">
                                                <span className="font-black text-emerald-700 uppercase text-[9px]">Totale da pagare</span>
                                                <span className="font-black text-emerald-700">{fmtEur(stat.costoStimato)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className="pt-3 border-t-2 border-dashed border-slate-200 flex justify-between items-center">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                        <TrendingUp size={13}/> Totale Costi Collaboratori
                                    </span>
                                    <span className="text-xl font-black text-slate-900">
                                        {fmtEur(teamStats.reduce((acc, curr) => acc + curr.costoStimato, 0))}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm text-slate-400 font-medium text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200 flex flex-col items-center gap-2">
                                <User size={24} className="opacity-50"/>
                                Nessun collaboratore assegnato.<br/>
                                <span className="text-xs">Vai alla scheda "Incarichi" per iniziare.</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Pannello di verifica firma digitale con link pubblico OTP Service

/** Pagina pubblica di verifica firma di OTP Service: basta incollare il checksum. */
const OTP_VERIFY_URL = 'https://app.otpservice.io/signature';

interface BloccoVerificaFirmaProps {
    checksumMandato?: string | null;
    checksumSoggetto?: string | null;
}

const BloccoVerificaFirma: React.FC<BloccoVerificaFirmaProps> = ({ checksumMandato, checksumSoggetto }) => (
    <div className="bg-white border border-emerald-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-emerald-50 border-b border-emerald-200 px-5 py-4">
            <h4 className="font-bold text-emerald-800 uppercase tracking-wider text-xs flex items-center gap-2">
                <ShieldCheck size={16}/> Verifica Firma Digitale
            </h4>
            <p className="text-[10px] text-emerald-700 mt-1 leading-snug">
                Codice ufficiale OTP Service. Condividilo con terzi (cliente, controparte, giudice) per dimostrare l'autenticità della firma.
            </p>
        </div>
        <div className="p-4 space-y-3">
            {checksumMandato && (
                <RigaChecksum label="Mandato" icon={<FileText size={14}/>} checksum={checksumMandato}/>
            )}
            {checksumSoggetto && (
                <RigaChecksum label="Soggetto Investigato" icon={<User size={14}/>} checksum={checksumSoggetto}/>
            )}
        </div>
    </div>
);

const RigaChecksum: React.FC<{ label: string; icon: React.ReactNode; checksum: string }> = ({ label, icon, checksum }) => {
    const [copiato, setCopiato] = React.useState(false);

    const copia = async () => {
        try {
            await navigator.clipboard.writeText(checksum);
            setCopiato(true);
            setTimeout(() => setCopiato(false), 1500);
        } catch {
            // Clipboard API negata o non disponibile: il link resta sempre cliccabile.
        }
    };

    return (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                    {icon} {label}
                </span>
                {/*<a
                    href={OTP_VERIFY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 whitespace-nowrap"
                    title="Apre la pagina pubblica di verifica firma di OTP Service"
                >
                    Verifica <ExternalLink size={11}/>
                </a>*/}
            </div>
            <button
                onClick={copia}
                className="w-full font-mono text-[10px] text-left text-slate-700 break-all bg-white border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 rounded-lg p-2 transition-colors flex items-start gap-2"
                title="Clicca per copiare il codice"
            >
                <Copy size={11} className="text-slate-400 mt-0.5 shrink-0"/>
                <span className="flex-1">{checksum}</span>
                {copiato && <span className="text-emerald-700 font-bold text-[9px] mt-0.5 shrink-0">COPIATO</span>}
            </button>
        </div>
    );
};